import type { BaseSyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import {
  commonFieldsSettings,
  type FormField,
  FormFieldContainer,
  FormFieldLayout,
  fieldValidators,
  SubmitButton,
  SubmitMessage,
  useFormDispatch,
  useFormSelector,
  useGetCountries,
  useWidgetSelector
} from '@elfsight/widget-form-builder/widget';
import { useEmitAPIEvent } from '@elfsight/widget-js-api';
import { Loader, Typography } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { API_SUBMISSION_FORM_SERVICE_NAME } from '../../../../constants';
import type { CustomFormField } from '../../../../types';
import { fieldsStyles } from '../../../form/fields-styles';
import { useEmailSubmitPopup } from '../../../use-email-submit-popup';
import { useRegisterFormService } from '../../../use-register-form-service';
import { fieldComponents } from '../fields/fieldComponents';
import { getProcessedSubmitFields } from '../utils';

const numberFormatter = (value: number) => {
  return value.toString();
};

type FormLayoutProps = {
  onSubmit?(e: BaseSyntheticEvent): Promise<string | undefined>;
  fields: CustomFormField[];
};

export function FormLayout({ fields, onSubmit }: FormLayoutProps) {
  const {
    fieldStyle,
    textColor,
    fieldAccentColor,
    calculatorEmailSubmitFields,
    resultCTAHeading,
    resultCTACaption,
    submitButtonText,
    submitAction,
    submitMessageTitle,
    submitMessageText,
    submitMessageTitleFontStyle,
    submitMessageTextFontStyle,
    submitMessageButton,
    submitMessageButtonText,
    recaptcha,
    language
  } = useSettings();
  const { t } = useLingui();

  const fieldStyleParameters = fieldsStyles[fieldStyle];

  const [error, setError] = useState<string | undefined>(undefined);
  const { submitted } = useWidgetSelector();
  const { status = '', fieldsError, fields: derivedFields } = useFormSelector();
  const dispatch = useFormDispatch();
  const emitAPIEvent = useEmitAPIEvent();

  const { countries } = useGetCountries();

  useRegisterFormService({
    serviceName: API_SUBMISSION_FORM_SERVICE_NAME,
    fields
  });

  const {
    closeFormPopup,
    popupStyles: { headerFontStyle, captionFontStyle, errorColor }
  } = useEmailSubmitPopup();
  const popupFields = fields.filter((field) => !field.isCalculatorField);

  const handleSubmit = async (e: BaseSyntheticEvent) => {
    if (!onSubmit) {
      return;
    }

    const processedSubmitFields = getProcessedSubmitFields(
      derivedFields,
      popupFields
    );

    const submitError = await onSubmit(e);

    if (submitError && !error) {
      setError(submitError);
    }

    emitAPIEvent(
      'submit',
      [processedSubmitFields],
      API_SUBMISSION_FORM_SERVICE_NAME
    );
  };

  useEffect(() => {
    const invalid = calculatorEmailSubmitFields.some((field: FormField) => {
      const item = derivedFields[field.id];
      return item?.valid === false && item?.value;
    });

    if (invalid !== fieldsError.invalid) {
      dispatch({
        type: 'setError',
        fieldsError: { invalid },
        status: undefined
      });
    }
  }, [calculatorEmailSubmitFields, derivedFields, dispatch]);

  useEffect(() => {
    const required = calculatorEmailSubmitFields.some((field: FormField) => {
      const { required, value } = derivedFields[field.id] || {};

      const isNonEmptyString = typeof value === 'string' && !!value.length;
      const isNonEmptyArray = Array.isArray(value) && !!value.length;
      const isNonEmptyNumber = typeof value === 'number' && !!value;

      const isNonEmpty =
        isNonEmptyString || isNonEmptyArray || isNonEmptyNumber;

      return required && !isNonEmpty;
    });
    if (required !== fieldsError.required) {
      dispatch({
        type: 'setError',
        fieldsError: { required },
        status: undefined
      });
    }
  }, [derivedFields, calculatorEmailSubmitFields, fieldsError, dispatch]);

  useEffect(() => {
    if (status === 'failed') {
      setError(t`An error occurred when submitting the form, please try again`);
      return;
    }

    if (fieldsError.required) {
      setError(t`Please fill all the required fields`);
      return;
    }

    if (fieldsError.invalid) {
      setError(t`Please fill all the fields correctly`);
      return;
    }

    setError(undefined);
  }, [status, fieldsError, t]);

  if (submitAction === 'message' && submitted && status === 'success') {
    return (
      <SubmitMessage
        fields={popupFields}
        fullWidth
        submitMessageTitle={submitMessageTitle}
        submitMessageText={submitMessageText}
        submitMessageTitleColor={textColor}
        submitMessageTextColor={textColor}
        submitMessageTitleFontStyle={submitMessageTitleFontStyle}
        submitMessageTextFontStyle={submitMessageTextFontStyle}
        submitMessageButton={submitMessageButton}
        submitMessageButtonText={submitMessageButtonText}
        submitMessageButtonColor={fieldAccentColor}
        onSubmitMessageButtonClick={closeFormPopup}
      />
    );
  }

  return (
    <Container onSubmit={(e) => e.preventDefault()}>
      {resultCTAHeading && (
        <Typography
          textStyle={headerFontStyle}
          color={textColor}
          style={{ marginBottom: '8px' }}
        >
          {resultCTAHeading}
        </Typography>
      )}

      {resultCTACaption && (
        <Typography
          textStyle={captionFontStyle}
          color={textColor}
          style={{ marginBottom: '32px' }}
          dangerouslySetInnerHTML={{ __html: resultCTACaption }}
        />
      )}

      {fields
        .filter((field) => !field.isCalculatorField)
        .map((field: any) => {
          const Component =
            fieldComponents[field.type as keyof typeof fieldComponents];
          const validator =
            fieldValidators[field.type as keyof typeof fieldValidators];

          return (
            <FormFieldContainer
              key={field.id}
              id={field.id}
              validator={validator}
            >
              <FieldLayout
                {...field}
                fieldLabelFontStyle={fieldStyleParameters.fieldLabel}
                fieldLabelColor={textColor}
                placeholderColor={getColorWithAlpha(textColor, 0.5)}
                padding={[10, 10, 10, 15]}
                fieldFontStyle={
                  ['choice'].includes(field.type)
                    ? commonFieldsSettings.fieldFontStyle
                    : fieldStyleParameters.fieldValue
                }
                fieldBorderType={
                  ['choice'].includes(field.type)
                    ? null
                    : fieldStyleParameters.fieldStyle === 'outline'
                      ? 'outline'
                      : null
                }
                fieldFillColor={
                  fieldStyleParameters.fieldStyle === 'outline' &&
                  field.type !== 'choice'
                    ? 'transparent'
                    : getColorWithAlpha(textColor, 0.05)
                }
                fieldBorderColor={getColorWithAlpha(textColor, 0.2)}
                fieldBorderWidth={1}
                fieldBorderRadius={4}
                required={field.required}
                fieldColor={textColor}
                fieldActiveColor={fieldAccentColor}
                fieldInsideLabelColor={textColor}
                fieldHintColor={getColorWithAlpha(textColor, 0.7)}
                fieldLabelPosition={'outside'}
                fieldInsideLabelFontStyle={
                  commonFieldsSettings.fieldInsideLabelFontStyle
                }
                fieldSpacing={32}
                fieldPadding={0}
                variants={{
                  focused: {
                    borderColor: fieldAccentColor
                  },
                  error: {
                    borderColor: errorColor
                  }
                }}
                invalidErrorMessage={t`Please enter a valid ${field.name}`}
                requiredErrorMessage={t`This field is required`}
                searchPlaceholderText={t`Search country...`}
                ariaLabelCountryDropdown={t`Country selection list`}
                ariaLabelCountryDropdownTrigger={t`Toggle country selection`}
                emptyCountryListMessage={t`No countries found`}
                countries={countries}
                widgetLanguage={language.language}
              >
                <Component {...field} numberFormatter={numberFormatter} />
              </FieldLayout>
            </FormFieldContainer>
          );
        })}

      {status === 'pending' ? (
        <Loader color={fieldAccentColor} />
      ) : (
        <SubmitButton
          text={submitButtonText}
          fullWidth
          color={fieldAccentColor}
          fontColor={textColor}
          font={{ fontSize: 15, fontWeight: 'bold' }}
          onClick={handleSubmit}
        />
      )}

      {['failed', 'invalid'].includes(status) && error && (
        <ErrorsGroup>
          <Error style={{ color: errorColor }}>{error}</Error>
        </ErrorsGroup>
      )}

      {recaptcha && (
        <Recaptcha _linksColor={fieldAccentColor}>
          <Typography
            color={getColorWithAlpha(textColor, 0.5)}
            textStyle={{
              fontSize: 13,
              lineHeight: 1.2
            }}
            dangerouslySetInnerHTML={{
              __html: t`This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="nofollow noreferrer noopener">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="nofollow noreferrer noopener">Terms of Service</a> apply.`
            }}
          />
        </Recaptcha>
      )}
    </Container>
  );
}

const Container = styled.form`
  display: block;
`;

const FieldLayout = styled(FormFieldLayout)``;

const Error = styled.div`
  font-size: 14px;
  line-height: 1.2;
`;

const ErrorsGroup = styled.div`
  margin-top: 12px;
`;

const Recaptcha = styled.div<{
  _linksColor: string;
}>`
  margin-top: 20px;

  & a {
    color: ${({ _linksColor }) => _linksColor};
    text-decoration: underline;
    cursor: pointer;
  }
`;
